import { emailSettingsStore } from '#lib/stores/emailSettings.js';

import { smsPacksStore, smsSettingsStore } from '#lib/stores/smsSettings.js';
import { apiAppBusinessLoadingappLoadall } from '#lib/types/generated/api.gen.js';
import { hydrateBusinessLoadAll } from '#lib/types/generated/businessLoadAll.gen.js';
import { clearWidgetConfig } from '#lib/widget/core.js';
import { DateTime } from 'luxon';
import { get, writable } from 'svelte/store';
import { now, businessTimezone } from '#lib/stores/time.js';
import { loadScansHistoryFromApi } from '../qr-code/qr-code';
import { activityStore } from './activity';
import { agendaSettingStore } from './agenda';
import { areaStore } from './area';
import { contractStore, refreshPaymentMethod } from './billing';
import { initBlockingPeriods } from './blocking-period';
import { clearBookingStore, setBookingListeners } from './booking';
import { bookingEventStore } from './bookingEvent';
import { businessStore } from './business';
import { resetDates } from './calendarDates';
import { disableChat } from './chat';
import { customFieldStore } from './custom-field';
import { featureFlipStore } from './feature-flip';
import { featuresClicked, wishListFeatureStore } from './features';
import { bookableGiftcardStore, giftcardStatsStore, setGiftcards } from './giftcard';
import { googleAuthStore, googleCalendarStore } from './google-calendar';
import { memoStore } from './memo';
import { loadNotificationStatsFromData } from './notification';
import { notificationSettingStore } from './notification-setting';
import { offerCollectionStore, offerStore } from './offer';
import { openingHourStore } from './opening-hour';
import { initOpeningHourExceptions } from './opening-hour-exception';
import { packageStore, setCustomerPackages } from './package';
import { productStore } from './product';
import { promoCodeStore } from './promoCode';
import { releaseNoteStore } from './releaseNote';
import { sessionStore } from './session';
import { clearSessionInstanceStore } from './session-instance';
import { setNextSocketToken } from './socket';
import { initSocketWatchers } from './socket-watchers';
import { checkStripeActionRequired } from './stripe';
import { tagStore } from './tags';
import { userStore } from './user';

let lastRefresh: DateTime = DateTime.now();
let currentUserId = '';

userStore.subscribe((user) => {
	if (currentUserId && user.id && currentUserId !== user.id) {
		refreshAppData(true);
	}
	currentUserId = user.id;
});

export function refreshAppData(force: boolean = false) {
	// If the last refresh was more than 10 seconds ago, refresh the data
	if (force || DateTime.now().diff(lastRefresh).as('seconds') > 10) {
		refresh();
	}
}

function refresh() {
	lastRefresh = DateTime.now();
	appDataLoaded.set(false);
	apiAppBusinessLoadingappLoadall({ raw: true }).then((rawRes) => {
		businessTimezone.set(rawRes.business.timezone);
		const res = hydrateBusinessLoadAll(rawRes);
		setNextSocketToken(res.socketToken);
		businessStore.set(res.business);
		activityStore.set(res.activities);
		areaStore.set(res.areas);
		sessionStore.set(res.sessions);
		offerStore.set(res.offers);
		offerCollectionStore.set(res.offerCollections);
		openingHourStore.set(res.openingHours);
		contractStore.set(res.contract);
		tagStore.set(res.tags);
		agendaSettingStore.set(res.agendaSettings);
		featureFlipStore.set({ features: res.features });
		notificationSettingStore.set(res.notificationSettings);
		const initialStart = get(now).minus({ months: 2 }).startOf('month');
		const initialEnd = get(now).plus({ months: 12 }).endOf('month');
		initOpeningHourExceptions(res.openingHourExceptions, initialStart, initialEnd);
		initBlockingPeriods(res.blockingPeriods, initialStart, initialEnd);
		bookableGiftcardStore.set(res.bookableGiftcards);
		setGiftcards(res.giftcards);
		giftcardStatsStore.set(res.giftcardStats);
		customFieldStore.set(res.customFields);
		emailSettingsStore.set(res.emailSettings);
		smsSettingsStore.set(res.smsSettings);
		smsPacksStore.set(res.smsPacks);
		wishListFeatureStore.set(res.featuresVotes);
		featuresClicked.set(res.featuresClicked);
		productStore.set(res.products);
		packageStore.set(res.packages);
		setCustomerPackages(res.customerPackages);
		googleCalendarStore.set(res.googleCalendars);
		googleAuthStore.set(res.googleAuths);
		promoCodeStore.set(res.promoCodes);
		memoStore.set(res.memos);
		bookingEventStore.set(res.bookingEvents);
		releaseNoteStore.set(res.releaseNotes);

		disableChat();
		clearBookingStore();
		clearSessionInstanceStore();
		clearWidgetConfig();
		appDataLoaded.set(true);
		resetDates();
		setBookingListeners();
		refreshPaymentMethod();
		checkStripeActionRequired();

		//loadBookings();
		//loadSessionInstance();
		loadScansHistoryFromApi(res.scans);
		initSocketWatchers();
		loadNotificationStatsFromData(res.notificationStats);
	});
}

export const appDataLoaded = writable(false);
